/**
 * Founder Control Center · Sección 6 · Security · Sprint T-15.
 *
 * Single source of truth para Security Center. Reemplaza el Overview legacy
 * y el Event Log legacy por los nuevos tabs del Sprint T-15 que consumen
 * directamente los endpoints T-14 vía React Query (1 endpoint = 1 widget).
 *
 * Tabs (orden fijado por founder · Sprint T-15):
 *   1. Overview            → SecurityOverviewTab (T-15 · /api/auth/2fa/status, /api/security/api-keys, /api/superadmin/audit-events)
 *   2. 2FA                 → TwoFactorTab        (T-15 · /api/auth/2fa/*)
 *   3. API Keys            → ApiKeysTab          (T-15 · /api/security/api-keys/*)
 *   4. Audit Events        → AuditEventsTab      (T-15 · /api/superadmin/audit-events)
 *   5. Compliance          → ComplianceTab       (T-15 · /api/security/compliance/*)
 *   6. Rate Limits         → RateLimitsTab       (T-15 · /api/superadmin/audit-events?action=ratelimit.triggered · read-only)
 *   7. Checklist           → SecurityGovernance  (existente · CRUD persistido)
 *   8. Sesiones            → ActiveSessionsCard  (reutilizado de /components/security · cookie auth)
 *
 * Filosofía Sprint T-15:
 *   - 0 lógica de negocio en frontend
 *   - 1 endpoint = 1 widget
 *   - 0 librerías nuevas (Tailwind + lucide-react ya disponibles)
 *   - 0 modificaciones al backend (AI Platform Freeze respetado)
 */
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, ListChecks, ShieldWarning, Devices, Key, Lock, Shield,
  Gauge, ChartBar,
} from "@phosphor-icons/react";
import SecurityGovernance from "../SecurityGovernance";
import ActiveSessionsCard from "../../../components/security/ActiveSessionsCard";
import { api } from "../../../lib/api";
import SubTabs, { useSubTab } from "./SubTabs";

// Sprint T-15 · tabs
import SecurityOverviewTab from "../../../components/securityCenter/SecurityOverviewTab";
import TwoFactorTab from "../../../components/securityCenter/TwoFactorTab";
import ApiKeysTab from "../../../components/securityCenter/ApiKeysTab";
import AuditEventsTab from "../../../components/securityCenter/AuditEventsTab";
import ComplianceTab from "../../../components/securityCenter/ComplianceTab";
import RateLimitsTab from "../../../components/securityCenter/RateLimitsTab";

const ITEMS = [
  { key: "overview",   label: "Overview",     icon: ChartBar },
  { key: "twofa",      label: "2FA",          icon: Key },
  { key: "api-keys",   label: "API Keys",     icon: Shield },
  { key: "event-log",  label: "Audit Events", icon: ShieldWarning },
  { key: "compliance", label: "Compliance",   icon: Lock },
  { key: "rate-limit", label: "Rate Limits",  icon: Gauge },
  { key: "checklist",  label: "Checklist",    icon: ListChecks },
  { key: "sesiones",   label: "Sesiones",     icon: Devices },
];

export default function SecuritySection() {
  const [sub, setSub] = useSubTab("overview");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Compat retro: si entran con un valor desconocido, caer en overview limpio.
  useEffect(() => {
    const current = searchParams.get("sub");
    if (current && !ITEMS.find((i) => i.key === current)) {
      navigate("/founder/security?sub=overview", { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-5" data-testid="fcc-security-section">
      <Header />
      <SubTabs items={ITEMS} value={sub} onChange={setSub} testid="security-subtabs" />
      <div className="pt-2">
        {sub === "overview"   && <SecurityOverviewTab />}
        {sub === "twofa"      && <TwoFactorTab />}
        {sub === "api-keys"   && <ApiKeysTab />}
        {sub === "event-log"  && <AuditEventsTab />}
        {sub === "compliance" && <ComplianceTab />}
        {sub === "rate-limit" && <RateLimitsTab />}
        {sub === "checklist"  && <SecurityGovernance />}
        {sub === "sesiones"   && <SesionesPanel />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
        <ShieldCheck size={11} weight="fill" /> Founder Control Center · Security
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
        Seguridad y gobernanza
      </h1>
      <p className="text-xs text-[var(--ag-text-dim)] mt-1">
        Overview · 2FA · API Keys · Audit Events · Compliance · Rate Limits · Checklist · Sesiones
      </p>
    </div>
  );
}

/**
 * Wrapper que reutiliza ActiveSessionsCard (rol="user") en el shell founder.
 * No duplica lógica: el componente vive en /components/security/ y se importa
 * tal cual con `api` autenticada (cookie httpOnly ag_user_token).
 */
function SesionesPanel() {
  return (
    <div className="space-y-4" data-testid="security-sesiones-root">
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
          <Devices size={11} weight="fill" /> Security · Sesiones founder
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">
          Tus dispositivos conectados
        </h2>
        <p className="text-xs text-[var(--ag-text-dim)] mt-1">
          Lista de cada login activo contra tu <code>token_version</code> actual. Cerrar sesión en todos los dispositivos bumpea el contador e invalida atómicamente todas las cookies emitidas.
        </p>
      </header>
      <ActiveSessionsCard api={api} role="user" />
    </div>
  );
}
